/** 货架内格子的两种形态（`04-api-contract.md` §7.1 的 `kind`） */
export type CellKind = 'PRODUCT' | 'PROMOTION';

/** 编辑器里三个输入框的原始文本（受控输入一律存字符串，提交时才解析） */
export interface CellDraft {
  quantity: string;
  purchaseLimit: string;
  sortOrder: string;
}

export type BuiltBody =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; error: string };

/**
 * 数量只对单品格子有意义：方案不持有库存（§7.5 校验表、§7.1 关键语义）。
 *
 * **购买上限同样只对单品格子有意义**：§7.1 的方案格子样例里 `purchaseLimit` 是 `null`
 * （`04-api-contract.md:880`），API 侧对方案格子传 `purchaseLimit` 返回
 * 400 `SCHEME_HAS_NO_STOCK`（§1.4 `:183`）。放行会写出「库里存着上限、网格接口却报 null」
 * 的自相矛盾状态。`05-ui-design-system.md` §2.2.2 R1 允许两处都设上限，
 * 与契约 §7.1 冲突，**以契约为准**。
 */
export function showsQuantity(kind: CellKind): boolean {
  return kind === 'PRODUCT';
}

/** 购买上限输入框的可见性，理由同上 */
export function showsPurchaseLimit(kind: CellKind): boolean {
  return kind === 'PRODUCT';
}

/** 非负整数；空串/非整数/负数一律 null */
function parseNonNegativeInt(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value < 0) return null;
  return value;
}

/**
 * 可选的 ≥ 1 整数：空串返回 `null`，由调用方决定 `null` 的含义。
 *
 * 两个调用点的语义**故意不同**（`purchaseLimit` 是可空列 `Int?`，契约 §1.2）：
 *   · **创建**（`buildCreateItemBody`）：空串 = 不设上限 → 请求体里**省略**该字段
 *     （新格子本来就是 `null`，省略与显式 `null` 结果相同）；
 *   · **更新**（`buildUpdateItemBody`）：空串 = 不限购 → **显式发 `null`**
 *     （PATCH 里「不传」= 保持原值，省略根本清不掉已有的上限）。
 */
function parseOptionalLimit(text: string): { ok: true; value: number | null } | { ok: false } {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, value: null };
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value < 1) return { ok: false };
  return { ok: true, value };
}

interface CreateInput {
  kind: CellKind;
  productId?: string | undefined;
  schemeId?: string | undefined;
  draft: CellDraft;
}

/**
 * 组装 `POST /shelves/:id/items` 的请求体。
 *
 * **方案格子绝不带 `quantity`**：契约 §7.5 明写「方案格子传值即 400」。
 * 用「按 kind 分支显式构造」而不是「展开整个表单 state」，就是为了让这条
 * 约束在类型与测试里都看得见。
 */
export function buildCreateItemBody(input: CreateInput): BuiltBody {
  const sortOrder = parseNonNegativeInt(input.draft.sortOrder);
  if (sortOrder === null) return { ok: false, error: '排序必须是不小于 0 的整数' };

  const limit = parseOptionalLimit(input.draft.purchaseLimit);
  if (!limit.ok) return { ok: false, error: '购买上限必须是不小于 1 的整数' };

  if (input.kind === 'PROMOTION') {
    if (!input.schemeId) return { ok: false, error: '请选择促销方案' };
    // 只发 schemeId 与 sortOrder：quantity 与 purchaseLimit 都会被 400 SCHEME_HAS_NO_STOCK 拒掉
    return { ok: true, body: { schemeId: input.schemeId, sortOrder } };
  }

  if (!input.productId) return { ok: false, error: '请选择商品' };
  const quantity = parseNonNegativeInt(input.draft.quantity);
  if (quantity === null) return { ok: false, error: '数量必须是不小于 0 的整数' };

  return {
    ok: true,
    body: {
      productId: input.productId,
      quantity,
      sortOrder,
      ...(limit.value === null ? {} : { purchaseLimit: limit.value }),
    },
  };
}

/**
 * 组装 `PATCH /shelf-items/:id` 的请求体（契约 §7 端点表第 15 行：数量/上限/排序）。
 *
 * 单品格子发 `quantity` / `purchaseLimit` / `sortOrder`；方案格子**只发 `sortOrder`**。
 *
 * **`purchaseLimit` 留空时发 `null`（不是省略）**：`ShelfItem.purchaseLimit` 是可空列
 * （`Int?`），PATCH 里「不传」= 保持原值、`null` = 不限购 —— 省略会让「清除上限」
 * 变成静默无操作。服务端自附录 B 偏差 59 起接受 `null`（并显式写库 NULL）。
 */
export function buildUpdateItemBody(kind: CellKind, draft: CellDraft): BuiltBody {
  const sortOrder = parseNonNegativeInt(draft.sortOrder);
  if (sortOrder === null) return { ok: false, error: '排序必须是不小于 0 的整数' };

  const limit = parseOptionalLimit(draft.purchaseLimit);
  if (!limit.ok) return { ok: false, error: '购买上限必须是不小于 1 的整数' };

  if (kind === 'PROMOTION') {
    // 方案格子只接受 sortOrder（§7.1 的样例里 shelfQuantity 与 purchaseLimit 恒为 null）
    return { ok: true, body: { sortOrder } };
  }

  const quantity = parseNonNegativeInt(draft.quantity);
  if (quantity === null) return { ok: false, error: '数量必须是不小于 0 的整数' };

  return {
    ok: true,
    body: {
      quantity,
      sortOrder,
      // 留空 → `null` = 不限购（服务端落库 NULL）；有值 → 该上限
      purchaseLimit: limit.value,
    },
  };
}

/**
 * 组装 `POST /shelf-items/:id/adjust-quantity` 的请求体（契约 §7.6）。
 *
 * 契约只给了示例 `{ "delta": -3, "reason": "补货" }`、**没有校验表**
 * （`:1088-1094`），因此这里只做两条最小约束：增量非 0（0 是无意义写入）、
 * 原因非空（日志要能读懂）；其余交给服务端。
 */
export function buildAdjustBody(deltaText: string, reason: string): BuiltBody {
  const delta = Number(deltaText.trim());
  if (!Number.isInteger(delta) || delta === 0) {
    return { ok: false, error: '增减数量必须是非 0 整数' };
  }
  const trimmedReason = reason.trim();
  if (trimmedReason === '') return { ok: false, error: '请填写原因' };
  return { ok: true, body: { delta, reason: trimmedReason } };
}
